import React from 'react';
import { X, CheckCircle, XCircle, Clock, Copy, ExternalLink, Smartphone, Zap, Calendar, Hash, DollarSign, Info, Wifi } from 'lucide-react';
import { toast } from 'react-hot-toast';

const TransactionDetailsModal = ({ transaction, onClose }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'AIRTIME':
        return <Smartphone className="h-5 w-5 text-blue-600" />;
      case 'DATA':
        return <Wifi className="h-5 w-5 text-purple-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const getServiceBg = (type) => {
    switch (type) {
      case 'AIRTIME':
        return 'bg-blue-50';
      case 'DATA':
        return 'bg-purple-50';
      default:
        return 'bg-gray-50';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-3 sm:px-4 pt-4 sm:pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-none">
          <div className="absolute top-0 right-0 pt-2 sm:pt-4 pr-2 sm:pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl sm:mx-0 shadow-lg ${
              transaction.status === 'SUCCESS' ? 'bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/30' :
              transaction.status === 'PENDING' ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-yellow-500/30' :
              'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30'
            }`}>
              {getServiceIcon(transaction.serviceType)}
            </div>
            <div className="mt-3 sm:mt-0 text-center sm:text-left w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center justify-center sm:justify-start mb-1">
                    <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                      Transaction Details
                    </h3>
                  </div>
                  <p className="text-[11px] sm:text-sm text-gray-500">
                    {transaction.serviceType} transaction
                  </p>
                </div>
                <span className={`inline-flex items-center justify-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium border ${getStatusColor(transaction.status)} shadow-neomorph-inset w-fit mx-auto sm:mx-0`}>
                  {getStatusIcon(transaction.status)}
                  <span className="ml-1 sm:ml-1.5">{transaction.status}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-5">
            {/* Transaction Info Grid - Single column on mobile, two columns on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {/* Left Column */}
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3 flex items-center">
                    <Info className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-gray-500" />
                    Transaction Information
                  </h4>
                  <div className="space-y-2.5 sm:space-y-3">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Reference</p>
                      <div className="flex items-center justify-between bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset">
                        <p className="text-[10px] sm:text-xs font-mono text-gray-800 truncate flex-1 mr-2">
                          {transaction.reference || transaction.vtuReference}
                        </p>
                        <button
                          onClick={() => handleCopy(transaction.reference || transaction.vtuReference)}
                          className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-md p-1 shadow-neomorph flex-shrink-0"
                        >
                          <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Provider Reference</p>
                      <p className="text-[10px] sm:text-xs font-mono text-gray-800 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset break-words">
                        {transaction.providerReference || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Date & Time</p>
                      <div className="flex items-center bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset">
                        <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <p className="text-[10px] sm:text-xs text-gray-800">{formatDate(transaction.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3 flex items-center">
                    <Smartphone className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-gray-500" />
                    Recipient Details
                  </h4>
                  <div className="space-y-2.5 sm:space-y-3">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Phone Number</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-800 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset break-words">
                        {transaction.recipient}
                      </p>
                    </div>
                    {transaction.productDetails?.network && (
                      <div>
                        <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Network</p>
                        <p className="text-xs sm:text-sm font-medium text-gray-800 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset">
                          {transaction.productDetails.network}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3 flex items-center">
                    <DollarSign className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-gray-500" />
                    Amount Details
                  </h4>
                  <div className="space-y-2.5 sm:space-y-3">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Amount</p>
                      <p className="text-xl sm:text-2xl font-bold text-primary-600 bg-white rounded-lg p-2 sm:p-3 shadow-neomorph-inset">
                        {formatAmount(transaction.amount)}
                      </p>
                    </div>
                    {transaction.productDetails?.name && (
                      <div>
                        <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Product</p>
                        <p className="text-xs sm:text-sm text-gray-800 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset break-words">
                          {transaction.productDetails.name}
                        </p>
                      </div>
                    )}
                    {transaction.productDetails?.validity && (
                      <div>
                        <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Validity</p>
                        <p className="text-xs sm:text-sm text-gray-800 bg-white rounded-lg p-1.5 sm:p-2 shadow-neomorph-inset">
                          {transaction.productDetails.validity}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Receipt Information - Responsive grid */}
            {transaction.receipt && (
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3 flex items-center">
                  <Hash className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-gray-500" />
                  Receipt Information
                </h4>
                
                {/* Status Badge */}
                {transaction.receipt.content?.transactions?.status && (
                  <div className="mb-2.5 sm:mb-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-[10px] sm:text-xs font-medium border ${
                      transaction.receipt.content.transactions.status === 'delivered' 
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    } shadow-neomorph-inset`}>
                      {transaction.receipt.content.transactions.status === 'delivered' ? (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      ) : (
                        <Clock className="h-3 w-3 mr-1" />
                      )}
                      {transaction.receipt.content.transactions.status}
                    </span>
                  </div>
                )}

                {/* Receipt Details Grid - Single column on mobile, two columns on tablet+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {transaction.receipt.content?.transactions?.product_name && (
                    <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Product</p>
                      <p className="text-[10px] sm:text-xs font-medium text-gray-800 break-words">
                        {transaction.receipt.content.transactions.product_name}
                      </p>
                    </div>
                  )}
                  
                  {transaction.receipt.content?.transactions?.unit_price && (
                    <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Unit Price</p>
                      <p className="text-[10px] sm:text-xs font-medium text-gray-800">
                        ₦{transaction.receipt.content.transactions.unit_price}
                      </p>
                    </div>
                  )}
                  
                  {transaction.receipt.content?.transactions?.transactionId && (
                    <div className="col-span-1 sm:col-span-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Provider Transaction ID</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] sm:text-xs font-mono text-gray-800 break-words flex-1">
                          {transaction.receipt.content.transactions.transactionId}
                        </p>
                        <button
                          onClick={() => handleCopy(transaction.receipt.content.transactions.transactionId)}
                          className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-md p-1 shadow-neomorph flex-shrink-0"
                        >
                          <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {transaction.receipt.response_description && (
                    <div className="col-span-1 sm:col-span-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Response</p>
                      <p className="text-[10px] sm:text-xs text-gray-800 break-words">{transaction.receipt.response_description}</p>
                    </div>
                  )}
                  
                  {transaction.receipt.code && (
                    <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Response Code</p>
                      <p className="text-[10px] sm:text-xs font-mono text-gray-800">{transaction.receipt.code}</p>
                    </div>
                  )}
                  
                  {transaction.receipt.requestId && (
                    <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                      <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Request ID</p>
                      <p className="text-[10px] sm:text-xs font-mono text-gray-800 break-words">{transaction.receipt.requestId.slice(0, 20)}...</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Metadata - Responsive grid */}
            {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-[11px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3">Additional Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(transaction.metadata).map(([key, value]) => (
                    typeof value !== 'object' && (
                      <div key={key} className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                        <p className="text-[9px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-800 break-words">{String(value)}</p>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons - Responsive layout */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
              {transaction.status === 'FAILED' && (
                <button
                  onClick={() => {
                    // Handle retry
                    onClose();
                  }}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium text-white"
                >
                  Retry Transaction
                </button>
              )}
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 border border-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default TransactionDetailsModal;